import { cn } from "nazare-ui/utils";
import styles from "./styles/loading-bar.module.css";

/**
 * Indeterminate "a NEW request is in flight" bar — a two-tone sweep along a
 * container's top edge. Absolutely positioned (`inset-x: 0; top: 0`), so the
 * caller just needs `position: relative` on whatever it should hug the top
 * of; it never reflows sibling content the way a real loading row/spinner
 * would.
 *
 * Deliberately narrower in scope than it looks: this is for the
 * `isFetching && isPlaceholderData` case specifically (TanStack Query v5's
 * `placeholderData: keepPreviousData` — the OLD result set is still on
 * screen while a request for a genuinely NEW query key is in flight), not
 * every background refetch. A same-key background refetch (e.g. window
 * refocus) re-confirming data that's already showing doesn't need this —
 * nothing the user did is waiting on it. See notifications/index.tsx's
 * `useSearchUsers` call site for the actual condition.
 *
 * `topOffset` (default 0) sits the bar below however many pixels of real
 * header content it shouldn't sweep across — e.g. the notifications matrix's
 * two-row `<thead>`, so the bar reads as "the ROWS are updating" rather than
 * painting over the header itself.
 */
export function LoadingBar({ className, topOffset = 0 }: { className?: string; topOffset?: number }) {
  return (
    <div
      className={cn(styles.loadingBar, className)}
      style={topOffset ? { top: topOffset } : undefined}
      aria-hidden="true"
    >
      <div className={styles.sweep} />
    </div>
  );
}
